// Default branding generator for companies without custom branding
import { supabase } from './supabase'

/**
 * Default branding templates with Prosperia branding
 */
export const DEFAULT_BRANDING_TEMPLATES = {
  prosperia: {
    name: 'Prosperia Default',
    primary_color: '#2563eb', // Blue
    accent_color: '#87ceeb',  // Light blue
    text_color: '#333333',    // Dark gray
    brand_color: '#2563eb',
    header_layout: 'minimalist',
    template: 'modern',
    font_heading: 'Montserrat',
    font_body: 'Lato',
    selected_palette: 'trust-blue',
    logo_url: '/icons/logo_1024x1024-noBGm.png', // Prosperia logo
    intro_text: 'Thank you for considering our services. We look forward to working with you.',
    outro_text: 'If you have any questions about this estimate, please don\'t hesitate to contact us.',
    footer_text: 'Professional service you can trust.'
  },
  modern: {
    name: 'Modern Professional',
    primary_color: '#1d4ed8', // Trust blue
    accent_color: '#60a5fa',   // Light blue
    text_color: '#1f2937',    // Dark gray
    brand_color: '#1d4ed8',
    header_layout: 'minimalist',
    template: 'modern',
    font_heading: 'Montserrat',
    font_body: 'Lato',
    selected_palette: 'trust-blue',
    logo_url: '/icons/logo_1024x1024-noBGm.png',
    intro_text: 'We appreciate the opportunity to provide you with this estimate.',
    outro_text: 'If you have any questions about this estimate, please don\'t hesitate to contact us.',
    footer_text: 'Professional service you can trust.'
  },
  classic: {
    name: 'Classic Business',
    primary_color: '#111827', // Dark gray
    accent_color: '#6b7280',   // Medium gray
    text_color: '#111827',     // Dark gray
    brand_color: '#111827',
    header_layout: 'centered',
    template: 'classic',
    font_heading: 'Playfair Display',
    font_body: 'Raleway',
    selected_palette: 'mono-gray',
    logo_url: '/icons/logo_1024x1024-noBGm.png',
    intro_text: 'We are pleased to present this estimate for your consideration.',
    outro_text: 'Thank you for your business. We look forward to serving you.',
    footer_text: 'Established excellence in service.'
  }
}

/**
 * Generate default branding data for a company
 * @param {string} companyId - The company ID
 * @param {Object} companyData - Company information from the companies table
 * @param {string} template - Template to use ('prosperia', 'modern', 'classic')
 * @returns {Object} Default branding data
 */
export const generateDefaultBranding = (companyId, companyData = {}, template = 'prosperia') => {
  const baseTemplate = DEFAULT_BRANDING_TEMPLATES[template] || DEFAULT_BRANDING_TEMPLATES.prosperia
  
  return {
    company_id: companyId,
    company_name: companyData.name || 'Your Company',
    logo_url: baseTemplate.logo_url,
    primary_color: baseTemplate.primary_color,
    accent_color: baseTemplate.accent_color,
    text_color: baseTemplate.text_color,
    brand_color: baseTemplate.brand_color,
    header_layout: baseTemplate.header_layout,
    template: baseTemplate.template,
    font_heading: baseTemplate.font_heading,
    font_body: baseTemplate.font_body,
    company_banner_url: '',
    selected_palette: baseTemplate.selected_palette,
    intro_text: baseTemplate.intro_text,
    outro_text: baseTemplate.outro_text,
    footer_text: baseTemplate.footer_text
  }
}

/**
 * Create default branding for a company if it doesn't exist
 * @param {string} companyId - The company ID
 * @param {string} template - Template to use ('prosperia', 'modern', 'classic')
 * @returns {Promise<Object>} Created branding data or existing data
 */
export const ensureDefaultBranding = async (companyId, template = 'prosperia') => {
  try {
    // Check if branding already exists
    const { data: existingBranding, error: checkError } = await supabase
      .from('company_branding')
      .select('*')
      .eq('company_id', companyId)
      .maybeSingle()

    if (checkError) {
      console.error('Error checking existing branding:', checkError)
      throw checkError
    }

    if (existingBranding) {
      console.log('Company branding already exists:', existingBranding.id)
      return existingBranding
    }

    // Get company data
    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .select('name, owner_first_name, owner_last_name, business_type, industry')
      .eq('id', companyId)
      .single()

    if (companyError) {
      console.error('Error fetching company data:', companyError)
      // Use fallback company data
      const fallbackCompanyData = { name: 'Your Company' }
      const defaultBranding = generateDefaultBranding(companyId, fallbackCompanyData, template)
      
      const { data: createdBranding, error: createError } = await supabase
        .from('company_branding')
        .insert([defaultBranding])
        .select()
        .single()

      if (createError) {
        console.error('Error creating default branding:', createError)
        throw createError
      }

      console.log('Created default branding with fallback data:', createdBranding.id)
      return createdBranding
    }

    // Generate and create default branding
    const defaultBranding = generateDefaultBranding(companyId, companyData, template)
    
    const { data: createdBranding, error: createError } = await supabase
      .from('company_branding')
      .insert([defaultBranding])
      .select()
      .single()

    if (createError) {
      console.error('Error creating default branding:', createError)
      throw createError
    }

    console.log('Created default branding:', createdBranding.id)
    return createdBranding

  } catch (error) {
    console.error('Error in ensureDefaultBranding:', error)
    throw error
  }
}

/**
 * Get branding data with automatic default creation
 * @param {string} companyId - The company ID
 * @param {string} template - Template to use ('prosperia', 'modern', 'classic')
 * @returns {Promise<Object>} Branding data (existing or newly created)
 */
export const getBrandingWithDefaults = async (companyId, template = 'prosperia') => {
  try {
    // Try to get existing branding
    const { data: branding, error } = await supabase
      .from('company_branding')
      .select('*')
      .eq('company_id', companyId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching branding:', error)
      throw error
    }

    if (branding) {
      return branding
    }

    // No branding exists, create default
    console.log('No custom branding found for company, creating default...')
    return await ensureDefaultBranding(companyId, template)

  } catch (error) {
    console.error('Error in getBrandingWithDefaults:', error)
    throw error
  }
}

/**
 * Update company branding API to use default generation
 * This can be used to replace the existing getCompanyBranding function
 */
export const getCompanyBrandingWithDefaults = async (companyId, options = {}) => {
  try {
    const template = options.template || 'prosperia'
    const branding = await getBrandingWithDefaults(companyId, template)
    
    // Return as array to match expected format in components
    return [branding]
  } catch (error) {
    console.error('Error in getCompanyBrandingWithDefaults:', error)
    return []
  }
}
